<script setup lang="ts">
/**
 * DangerZoneSection — permanent workspace actions (President only).
 *
 * D-S7: Data export button is disabled with tooltip "Coming soon".
 * D-S9: Delete workspace = 30-day soft-delete grace period.
 *   - Confirm modal: type HOA slug to enable Delete button.
 *   - On success: shows "Scheduled for deletion on {date}" banner + "Undo" button.
 */
import { ref, computed } from 'vue';
import dayjs from 'dayjs';
import BaseButton from '@/components/ui/BaseButton.vue';
import { useSettingsStore } from '@/stores/settings';

const store = useSettingsStore();

// Delete workspace modal state
const showDeleteModal = ref(false);
const confirmInput = ref('');
const deleteError = ref<string | null>(null);

const expectedSlug = computed(() => store.profile?.shortCode ?? '');

const deleteEnabled = computed(
  () => confirmInput.value.trim() === expectedSlug.value,
);

async function onDeleteConfirm() {
  if (!deleteEnabled.value) return;
  deleteError.value = null;
  try {
    await store.deleteWorkspace(confirmInput.value.trim());
    showDeleteModal.value = false;
    confirmInput.value = '';
  } catch (e) {
    deleteError.value = e instanceof Error ? e.message : 'Failed to delete workspace.';
  }
}

function onDeleteCancel() {
  showDeleteModal.value = false;
  confirmInput.value = '';
  deleteError.value = null;
}

// Undo delete
const undoError = ref<string | null>(null);

async function onUndoDelete() {
  undoError.value = null;
  try {
    await store.cancelDelete();
  } catch (e) {
    undoError.value = e instanceof Error ? e.message : 'Failed to cancel deletion.';
  }
}

function formatDate(d: string): string {
  return dayjs(d).format('MMMM D, YYYY');
}
</script>

<template>
  <div id="danger" class="border border-red-100 bg-red-50 rounded-lg p-5" data-testid="danger-zone-section">
    <h3 class="text-[15px] font-bold text-red-700 mb-1">Danger zone</h3>
    <p class="text-sm text-red-700 mb-4">Permanent actions that affect everyone in this workspace.</p>

    <!-- Soft-delete scheduled banner (D-S9) -->
    <div
      v-if="store.isDeleted && store.scheduledForHardDeleteAt"
      class="mb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 bg-amber-50 border border-amber-200 rounded-md"
      data-testid="danger-delete-banner"
    >
      <div>
        <p class="text-sm font-semibold text-amber-800">
          Workspace scheduled for deletion on {{ formatDate(store.scheduledForHardDeleteAt) }}
        </p>
        <p class="text-xs text-amber-700 mt-0.5">All data will be permanently deleted. You can undo this before that date.</p>
      </div>
      <BaseButton
        variant="secondary"
        size="sm"
        :loading="store.isSaving"
        data-testid="danger-undo-delete-btn"
        @click="onUndoDelete"
      >
        Undo delete
      </BaseButton>
      <p v-if="undoError" class="text-xs text-red-600">{{ undoError }}</p>
    </div>

    <!-- Row: Transfer ownership (stub) -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-b border-red-100/60">
      <div>
        <div class="text-sm font-semibold text-fg">Transfer workspace ownership</div>
        <div class="text-xs text-muted mt-0.5">Make another board member the workspace owner.</div>
      </div>
      <button
        class="h-8 px-3 rounded-md border border-border bg-surface text-sm font-medium text-fg hover:bg-canvas-soft transition-colors shrink-0"
        data-testid="danger-transfer-btn"
      >
        Transfer
      </button>
    </div>

    <!-- Row: Export all data (D-S7 disabled stub) -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-3 border-b border-red-100/60">
      <div>
        <div class="text-sm font-semibold text-fg">Export all data</div>
        <div class="text-xs text-muted mt-0.5">Download a ZIP of all issues, submissions, and audit log.</div>
      </div>
      <button
        disabled
        aria-disabled="true"
        title="Coming soon"
        class="h-8 px-3 rounded-md border border-border bg-canvas-soft text-sm font-medium text-muted cursor-not-allowed opacity-50 shrink-0"
        data-testid="danger-export-btn"
      >
        Request export
      </button>
    </div>

    <!-- Row: Delete workspace -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-3">
      <div>
        <div class="text-sm font-semibold text-red-700">Delete workspace</div>
        <div class="text-xs text-muted mt-0.5">
          Begins a 30-day soft-delete grace period. Hard-delete runs automatically after that.
        </div>
      </div>
      <BaseButton
        variant="danger"
        size="sm"
        :disabled="store.isDeleted"
        data-testid="danger-delete-workspace-btn"
        @click="showDeleteModal = true"
      >
        Delete workspace
      </BaseButton>
    </div>

    <!-- Delete confirmation modal -->
    <Teleport to="body">
      <div
        v-if="showDeleteModal"
        class="fixed inset-0 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delete-modal-title"
      >
        <div class="absolute inset-0 bg-black/40" @click="onDeleteCancel" />
        <div class="relative bg-surface border border-border rounded-xl shadow-xl p-6 max-w-md w-full z-10">
          <h3 id="delete-modal-title" class="text-base font-bold text-red-700 mb-2">
            Delete workspace?
          </h3>
          <p class="text-sm text-muted mb-4">
            This begins a 30-day soft-delete grace period. To confirm, type
            <strong class="font-mono text-fg">{{ expectedSlug }}</strong> below.
          </p>

          <label class="block text-sm font-medium text-fg mb-1" for="delete-confirm-input">
            Confirm by typing the portal slug
          </label>
          <input
            id="delete-confirm-input"
            v-model="confirmInput"
            type="text"
            class="w-full h-9 px-3 rounded-md border border-border bg-surface text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
            :placeholder="expectedSlug"
            data-testid="danger-confirm-input"
            @keydown.enter="onDeleteConfirm"
          >

          <p v-if="deleteError" class="text-sm text-red-600 mt-2" role="alert">{{ deleteError }}</p>

          <div class="flex gap-2 mt-4 flex-col sm:flex-row">
            <BaseButton
              variant="danger"
              size="sm"
              :disabled="!deleteEnabled"
              :loading="store.isSaving"
              data-testid="danger-confirm-delete-btn"
              @click="onDeleteConfirm"
            >
              Delete workspace
            </BaseButton>
            <BaseButton
              variant="secondary"
              size="sm"
              data-testid="danger-cancel-delete-btn"
              @click="onDeleteCancel"
            >
              Cancel
            </BaseButton>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>
